import { DomPatchOptions, DomPatchScheduler } from "../dom-patcher";
import { isSharedFetchHttpError, sharedFetchText } from "../shared/fetch-cache";
import { DEFAULT_LISTEN_SELECTOR, FrameAttribute, OBSERVED_ATTRIBUTES } from "./const";
import { resolveFrameConfig } from "./config";
import { findProxySourceElement, serializeProxyHtml } from "./proxy";
import {
  mergeQueryEntries,
  objectToQueryEntries,
  parseQueryInput,
  parseQueryString,
  queryEntriesToString,
} from "./query";
import {
  captureScrollState,
  resolveRenderableHtml,
  resolveRequestUrl,
  restoreScrollState,
  syncHistoryUrl,
} from "./runtime";
import {
  extractPayloadFromSubmitTargets,
  extractSubmitPayload,
  extractPayloadFromTriggerTarget,
  findDelegatedTarget,
  shouldPreventDefaultTrigger,
} from "./triggers";
import {
  FrameConfig,
  QueryMode,
  RemoteFrameBroadcastPayload,
  RemoteFrameErrorDetail,
  RemoteFrameRefreshPayload,
  RemoteFrameSubmitErrorDetail,
  RemoteFrameSubmitPayload,
} from "./types";
import {
  createAbortError,
  createSchedulerSignature,
  hasFrameDatasetAttributes,
  isAbortError,
  matchesRefreshTarget,
  nextFrame,
  normalizeRefreshPayload,
  parseCredentials,
  splitCsv,
  toNumber,
} from "./utils";

export class RemoteFrameElement extends HTMLElement {
  private static readonly historyChangeEventName = "remote-frame:historychange";
  private static historyChangeBridgeInstalled = false;
  private static lastHistoryUrl = "";
  private static internalHistoryChangeDepth = 0;
  private static internalHistoryChangeSourceFrameIds: string[] = [];

  /**
   * Returns the list of reactive attributes watched by the custom element.
   *
   * @returns Attribute names observed by `attributeChangedCallback`.
   */
  static get observedAttributes(): string[] {
    return [...OBSERVED_ATTRIBUTES];
  }

  private scheduler: DomPatchScheduler<HTMLElement> | null = null;
  private schedulerSignature = "";
  private activeRequestController: AbortController | null = null;
  private activeSubmitController: AbortController | null = null;
  private activeSubmitTimeoutId: number | null = null;
  private submitDebounceTimerId: number | null = null;
  private pendingSubmitContext:
    | {
        form: HTMLFormElement;
        submitter: HTMLElement | null;
        submitPayload: RemoteFrameSubmitPayload;
        refreshPayload: RemoteFrameRefreshPayload;
      }
    | null = null;
  private currentFrameUrl: string | null = null;
  private requestVersion = 0;

  private proxyObserver: MutationObserver | null = null;
  private proxySource: Element | null = null;
  private proxyRenderFrameId: number | null = null;

  private delegatedHandlers = new Map<string, EventListener>();
  private prefetchHandlers = new Map<string, EventListener>();
  private documentHandlers = new Map<string, EventListener>();
  private broadcastHandlers = new Map<string, EventListener>();

  private intersectionObserver: IntersectionObserver | null = null;
  private hasIntersected = false;
  private pollTimerId: number | null = null;

  private loading = false;
  private submitting = false;
  private hasCompletedInitialRender = false;
  private lastRenderedHtml: string | null = null;
  private skipAutoRefresh = false;
  private queuedRefresh = false;
  private queuedPayload: RemoteFrameRefreshPayload | null = null;
  private refreshQueueToken = 0;
  private skipNextQueryAttributeRefresh = false;
  private skipNextSourceSelectorAttributeRefresh = false;
  private pendingRelayContext:
    | {
        messageId: string;
        originFrameId: string;
        hop: number;
        maxHop: number;
      }
    | null = null;
  private seenBroadcastMessageIds = new Set<string>();
  private seenBroadcastQueue: string[] = [];

  private static broadcastSequence = 0;

  /** Re-evaluates polling whenever tab visibility changes. */
  private readonly onVisibilityChange = (): void => {
    this.updatePolling();
  };

  /** Reacts to history changes emitted by the global history bridge. */
  private readonly onHistoryChange = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as {
      action?: string;
      url?: string;
      internal?: boolean;
      sourceFrameId?: string;
    } | null;
    const action = detail?.action;
    if (detail?.internal && detail?.sourceFrameId === this.id) return;
    const url = (detail?.url ?? window.location.href).trim() || window.location.href;

    if (action !== "push" && action !== "replace" && action !== "pop") return;
    if (!this.shouldRefreshFromHistoryChange(action, url)) return;

    // History-driven refresh should follow the route URL that triggered the event.
    void this.refresh({ src: url });
  };

  /** Handles `remote-frame:refresh` events dispatched on or through this element. */
  private readonly onRefreshEvent = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail = normalizeRefreshPayload(event.detail);
    const target = detail.target ?? "closest";

    if (!matchesRefreshTarget(this, target, event)) return;

    if (detail.stopOnHandled) {
      event.stopPropagation();
    }

    void this.refresh(detail);
  };

  /** Initializes listeners and the first render cycle when the frame is attached. */
  connectedCallback(): void {
    if (!this.hasAttribute("data-state")) {
      this.setAttribute("data-state", "idle");
    }
    if (!this.hasAttribute("data-submit-state")) {
      this.setAttribute("data-submit-state", "idle");
    }

    RemoteFrameElement.ensureHistoryChangeBridge();
    this.addEventListener("remote-frame:refresh", this.onRefreshEvent as EventListener);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener(RemoteFrameElement.historyChangeEventName, this.onHistoryChange as EventListener);
    this.setupDelegatedListeners();
    this.setupPrefetchListeners();
    this.setupDocumentListeners();
    this.setupBroadcastListeners();
    this.updatePolling();

    if (this.hasAttribute(FrameAttribute.Lazy)) {
      this.setupLazyObserver();
    }

    if (this.shouldSkipInitialFetch()) {
      this.skipAutoRefresh = true;
      this.cancelQueuedRefresh();
      this.markHydratedFromExistingContent();
      return;
    }

    this.scheduleRefresh();
  }

  /** Disposes observers, listeners, timers, and in-flight work when detached. */
  disconnectedCallback(): void {
    this.removeEventListener("remote-frame:refresh", this.onRefreshEvent as EventListener);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener(RemoteFrameElement.historyChangeEventName, this.onHistoryChange as EventListener);
    this.abort();
    this.clearPendingSubmitDebounce();
    this.disconnectProxyObserver();
    this.disconnectIntersectionObserver();
    this.clearPollingTimer();
    this.clearDelegatedListeners();
    this.clearPrefetchListeners();
    this.clearDocumentListeners();
    this.clearBroadcastListeners();

    if (this.proxyRenderFrameId !== null) {
      cancelAnimationFrame(this.proxyRenderFrameId);
      this.proxyRenderFrameId = null;
    }

    this.pendingRelayContext = null;
    this.seenBroadcastMessageIds.clear();
    this.seenBroadcastQueue.length = 0;
    this.hasCompletedInitialRender = false;
    this.skipAutoRefresh = false;
    this.cancelQueuedRefresh();

    this.scheduler?.cancel();
  }

  /**
   * Reacts to attribute updates and applies the minimum required side effects.
   *
   * @param name - Attribute name that changed.
   * @param oldValue - Previous attribute value.
   * @param newValue - Next attribute value.
   */
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;
    if (name === FrameAttribute.Query && this.skipNextQueryAttributeRefresh) return;
    if (name === FrameAttribute.SourceSelector && this.skipNextSourceSelectorAttributeRefresh) return;

    if (name === FrameAttribute.ListenEvents || name === FrameAttribute.ListenSelector) {
      this.setupDelegatedListeners();
      return;
    }

    if (name === FrameAttribute.PrefetchEvents || name === FrameAttribute.PrefetchSelector) {
      this.setupPrefetchListeners();
      return;
    }

    if (name === FrameAttribute.DocumentEvents) {
      this.setupDocumentListeners();
      return;
    }

    if (
      name === FrameAttribute.BroadcastEventPrefix ||
      name === FrameAttribute.ListenRefreshFrom ||
      name === FrameAttribute.ListenRefreshUrlMode ||
      name === FrameAttribute.ListenRefreshQueryKeys ||
      name === FrameAttribute.BroadcastMaxHop ||
      name === FrameAttribute.BroadcastReemit
    ) {
      this.setupBroadcastListeners();
      return;
    }

    if (name === FrameAttribute.PollMs) {
      this.updatePolling();
      return;
    }

    if (name === FrameAttribute.InitialFetch) {
      if (this.shouldSkipInitialFetch()) {
        this.skipAutoRefresh = true;
        this.cancelQueuedRefresh();
        this.invalidateActiveRequest("Initial fetch disabled");
        this.markHydratedFromExistingContent();
        return;
      }

      this.skipAutoRefresh = false;
      this.scheduleRefresh();
      return;
    }

    if (name === FrameAttribute.Lazy) {
      if (this.hasAttribute(FrameAttribute.Lazy)) {
        this.setupLazyObserver();
      } else {
        this.hasIntersected = true;
        this.disconnectIntersectionObserver();
        this.updatePolling();
        this.scheduleRefresh();
      }
      return;
    }

    if (name === FrameAttribute.LoadingClass && this.loading) {
      if (oldValue) this.classList.remove(oldValue);
      const nextClass = newValue?.trim();
      if (nextClass) this.classList.add(nextClass);
      return;
    }

    if (name === FrameAttribute.SubmittingClass && this.submitting) {
      if (oldValue) this.classList.remove(oldValue);
      const nextClass = newValue?.trim();
      if (nextClass) this.classList.add(nextClass);
      return;
    }

    if (name === FrameAttribute.Debug) {
      return;
    }

    if (name === FrameAttribute.ListenHistory) {
      return;
    }

    this.scheduleRefresh();
  }

  /**
   * Aborts active network and submit work owned by this frame instance.
   */
  abort(): void {
    if (this.activeRequestController) {
      this.activeRequestController.abort(createAbortError("Manually aborted"));
      this.activeRequestController = null;
    }

    this.abortActiveSubmit("Manually aborted");
    this.clearPendingSubmitDebounce();
  }

  /**
   * Forces a frame refresh with optional payload overrides.
   *
   * @param payload - Refresh overrides applied for this invocation.
   * @returns Promise resolved after refresh handling completes.
   */
  refresh(payload: RemoteFrameRefreshPayload = {}): Promise<void> {
    this.skipAutoRefresh = false;
    return this.performRefresh(normalizeRefreshPayload(payload));
  }

  /**
   * Backward-compatible alias for `refresh()`.
   *
   * @param payload - Refresh overrides applied for this invocation.
   * @returns Promise resolved after refresh handling completes.
   */
  applyTrigger(payload: RemoteFrameRefreshPayload = {}): Promise<void> {
    return this.refresh(payload);
  }

  /**
   * Backward-compatible alias for `refresh()`.
   *
   * @param payload - Refresh overrides applied for this invocation.
   * @returns Promise resolved after refresh handling completes.
   */
  trigger(payload: RemoteFrameRefreshPayload = {}): Promise<void> {
    return this.refresh(payload);
  }

  /**
   * Updates query params on the frame and optionally refreshes with those values.
   *
   * @param params - Query params to merge or replace.
   * @param options - Query update options.
   * @returns Promise resolved after optional refresh completes.
   */
  setQueryParams(
    params: Record<string, string | number | boolean | null | undefined>,
    options: { mode?: QueryMode; refresh?: boolean } = {}
  ): Promise<void> {
    const mode = options.mode ?? "merge";
    const refreshAfter = options.refresh ?? true;

    const existing = parseQueryString(this.getAttribute(FrameAttribute.Query) ?? "");
    const incoming = objectToQueryEntries(params);

    const merged = mode === "replace" ? incoming : mergeQueryEntries(existing, incoming);
    const nextQuery = queryEntriesToString(merged);

    this.skipNextQueryAttributeRefresh = true;
    try {
      this.setAttribute(FrameAttribute.Query, nextQuery);
    } finally {
      this.skipNextQueryAttributeRefresh = false;
    }

    if (!refreshAfter) {
      return Promise.resolve();
    }

    return this.refresh({ query: params, queryMode: mode });
  }

  /** Coalesces multiple refresh requests so only the latest payload runs in this tick. */
  private scheduleRefresh(payload: RemoteFrameRefreshPayload = {}): void {
    if (this.skipAutoRefresh) {
      return;
    }

    this.queuedPayload = {
      ...(this.queuedPayload ?? {}),
      ...payload,
    };

    if (this.queuedRefresh) return;

    this.queuedRefresh = true;
    const scheduledToken = ++this.refreshQueueToken;
    queueMicrotask(() => {
      if (scheduledToken !== this.refreshQueueToken) return;
      this.queuedRefresh = false;
      const nextPayload = this.queuedPayload ?? {};
      this.queuedPayload = null;
      void this.refresh(nextPayload);
    });
  }

  /** Resolves config and executes either proxy rendering or remote fetching + patching. */
  private async performRefresh(payload: RemoteFrameRefreshPayload): Promise<void> {
    if (!this.isConnected) return;
    const effectivePayload = this.withFallbackSource(payload);
    this.syncSourceSelectorAttributeFromPayload(effectivePayload);

    const config = resolveFrameConfig(this, effectivePayload);
    this.debugLog(
      `refresh:start mode=${config.sourceSelector ? "proxy" : "network"} cache=${config.cache}`
    );

    if (config.lazy && !this.hasIntersected) {
      this.invalidateActiveRequest("Waiting for lazy intersection");
      this.setupLazyObserver();
      return;
    }

    if (config.sourceSelector) {
      this.invalidateActiveRequest("Switching to proxy source mode");
      await this.renderFromProxySource(config, effectivePayload);
      return;
    }

    this.disconnectProxyObserver();

    const requestUrl = resolveRequestUrl(config, effectivePayload);
    if (!requestUrl) {
      this.invalidateActiveRequest("No request URL");
      this.setFrameState("empty");
      return;
    }
    this.debugLog(`refresh:url ${requestUrl}`);

    if (
      this.loading &&
      this.activeRequestController &&
      config.cache === "default" &&
      this.currentFrameUrl === requestUrl
    ) {
      this.debugLog("refresh:dedupe-inflight");
      return;
    }

    this.currentFrameUrl = requestUrl;

    if (this.activeRequestController) {
      this.activeRequestController.abort(createAbortError("Superseded by newer request"));
    }

    this.requestVersion += 1;
    const version = this.requestVersion;
    const controller = new AbortController();
    this.activeRequestController = controller;

    this.setLoadingState(true, config.loadingClass);
    this.dispatchEvent(
      new CustomEvent("remote-frame:loadstart", {
        detail: { url: requestUrl, version },
      })
    );

    try {
      const response = await sharedFetchText(requestUrl, {
        cacheMode: config.cache,
        cacheTtlMs: config.cacheTtlMs,
        cacheMaxEntries: config.cacheMaxEntries,
        timeoutMs: config.timeoutMs,
        signal: controller.signal,
        credentials: config.credentials,
        debug: (event) => {
          if (
            event.phase !== "cache-hit" &&
            event.phase !== "cache-miss" &&
            event.phase !== "network-start" &&
            event.phase !== "network-success" &&
            event.phase !== "network-error"
          ) {
            return;
          }
          const status = typeof event.status === "number" ? ` status=${event.status}` : "";
          this.debugLog(`cache:${event.phase}${status}`);
        },
      });

      if (this.requestVersion !== version || controller.signal.aborted) {
        return;
      }

      const resolved = resolveRenderableHtml(this, response.bodyText, config);

      const patchSkipped = this.lastRenderedHtml === resolved.html;
      if (!patchSkipped) {
        const scrollSnapshot = captureScrollState(this, config);
        await this.patchHtml(resolved.html, config);
        restoreScrollState(this, scrollSnapshot, config);
        this.lastRenderedHtml = resolved.html;
      }

      this.runWithInternalHistoryChange(() => {
        syncHistoryUrl(requestUrl, config);
      });
      this.setFrameState(resolved.html.trim() ? "ready" : "empty");

      this.dispatchEvent(
        new CustomEvent("remote-frame:load", {
          detail: {
            url: requestUrl,
            responseUrl: response.responseUrl,
            status: response.status,
            fromCache: response.fromCache,
            matchedSelector: resolved.matchedSelector,
            patchSkipped,
            version,
          },
        })
      );
      this.debugLog(
        `refresh:success status=${response.status} fromCache=${response.fromCache ? "yes" : "no"}`
      );

      const isInitialRender = !this.hasCompletedInitialRender;
      this.hasCompletedInitialRender = true;
      this.emitRefreshBroadcast(config, "network", effectivePayload, requestUrl, isInitialRender);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      const detail = this.buildNetworkErrorDetail(requestUrl, version, error);
      this.setFrameState("error");
      this.dispatchEvent(
        new CustomEvent("remote-frame:error", {
          detail,
        })
      );
      this.debugLog(`refresh:error kind=${detail.kind}${detail.status ? ` status=${detail.status}` : ""}`);
    } finally {
      if (this.activeRequestController === controller) {
        this.activeRequestController = null;
      }

      if (this.requestVersion === version) {
        this.setLoadingState(false, config.loadingClass);
      }
    }
  }

  /** Renders mirrored content from an existing DOM source and keeps it synced. */
  private async renderFromProxySource(
    config: FrameConfig,
    payload: RemoteFrameRefreshPayload
  ): Promise<void> {
    const source = findProxySourceElement(this, config.sourceSelector, this.proxySource);
    if (!source) {
      this.disconnectProxyObserver();
      this.setFrameState("empty");
      const detail: RemoteFrameErrorDetail = {
        mode: "proxy",
        kind: "proxy-config",
        sourceSelector: config.sourceSelector,
        message: `source-selector did not match any element: ${config.sourceSelector}`,
        hint: "Ensure source-selector points to an element that exists before this frame refreshes.",
        error: new Error(`source-selector did not match any element: ${config.sourceSelector}`),
      };
      this.dispatchEvent(
        new CustomEvent("remote-frame:error", {
          detail,
        })
      );
      return;
    }

    if (source === this || source.contains(this)) {
      this.disconnectProxyObserver();
      this.setFrameState("error");
      const detail: RemoteFrameErrorDetail = {
        mode: "proxy",
        kind: "proxy-config",
        sourceSelector: config.sourceSelector,
        message: "source-selector cannot reference this frame or one of its ancestors",
        hint: "Point source-selector to a sibling or external DOM node.",
        error: new Error("source-selector cannot reference this frame or one of its ancestors"),
      };
      this.dispatchEvent(
        new CustomEvent("remote-frame:error", {
          detail,
        })
      );
      return;
    }

    if (this.proxySource !== source) {
      this.disconnectProxyObserver();
      this.proxySource = source;
      this.proxyObserver = new MutationObserver(() => {
        if (this.proxyRenderFrameId !== null) return;
        this.proxyRenderFrameId = requestAnimationFrame(() => {
          this.proxyRenderFrameId = null;
          void this.refresh();
        });
      });

      this.proxyObserver.observe(source, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
    }

    const html = serializeProxyHtml(source, config.select);
    const patchSkipped = this.lastRenderedHtml === html;
    if (!patchSkipped) {
      const scrollSnapshot = captureScrollState(this, config);
      await this.patchHtml(html, config);
      restoreScrollState(this, scrollSnapshot, config);
      this.lastRenderedHtml = html;
    }

    this.setFrameState(html.trim() ? "ready" : "empty");
    this.dispatchEvent(
      new CustomEvent("remote-frame:load", {
        detail: {
          mode: "proxy",
          sourceSelector: config.sourceSelector,
          patchSkipped,
        },
      })
    );

    const isInitialRender = !this.hasCompletedInitialRender;
    this.hasCompletedInitialRender = true;
    this.emitRefreshBroadcast(config, "proxy", payload, undefined, isInitialRender);
  }

  /** Builds DOM patcher options from the active frame configuration. */
  private patchOptionsFor(config: FrameConfig): DomPatchOptions {
    const preserveValueOrSelection =
      config.preserveState.has("value") || config.preserveState.has("selection");

    return {
      keyAttribute: "data-key",
      ignoreActiveValue: preserveValueOrSelection,
      restoreFocus: config.preserveState.has("focus") || config.preserveState.has("selection"),
      executeScripts: config.executeScripts,
    };
  }

  /** Enqueues HTML for DOM patching and waits for the next frame boundary. */
  private async patchHtml(html: string, config: FrameConfig): Promise<void> {
    this.ensurePatchScheduler(config);
    this.scheduler?.enqueueHTML(html);

    await nextFrame();
  }

  /** Recreates the patch scheduler when option-signature changes require it. */
  private ensurePatchScheduler(config: FrameConfig): void {
    const signature = createSchedulerSignature(config);
    if (!this.scheduler || signature !== this.schedulerSignature) {
      this.scheduler?.cancel();
      this.scheduler = new DomPatchScheduler(this, this.patchOptionsFor(config), "children");
      this.schedulerSignature = signature;
    }
  }

  /** Updates loading attributes/classes and accessibility busy state. */
  private setLoadingState(nextLoading: boolean, loadingClass: string): void {
    if (this.loading === nextLoading) return;
    this.loading = nextLoading;
    this.toggleAttribute("loading", nextLoading);
    this.classList.toggle(loadingClass, nextLoading);
    this.updateBusyState();
    if (nextLoading) {
      this.setAttribute("data-state", "loading");
    }
  }

  /** Sets the primary frame render state attribute. */
  private setFrameState(state: "ready" | "error" | "empty"): void {
    this.setAttribute("data-state", state);
    this.updateBusyState();
  }

  /** Updates submit lifecycle flags/classes and accessibility busy state. */
  private setSubmittingState(nextSubmitting: boolean, state: "idle" | "submitting" | "error"): void {
    if (this.submitting === nextSubmitting && this.getAttribute("data-submit-state") === state) return;
    this.submitting = nextSubmitting;
    this.toggleAttribute("submitting", nextSubmitting);
    this.classList.toggle(this.getActiveSubmittingClass(), nextSubmitting);
    this.setAttribute("data-submit-state", state);
    this.updateBusyState();
  }

  /** Reflects combined loading/submitting activity to `aria-busy`. */
  private updateBusyState(): void {
    const busy = this.loading || this.submitting;
    this.setAttribute("aria-busy", busy ? "true" : "false");
  }

  /** Maps network refresh failures into a normalized error detail payload. */
  private buildNetworkErrorDetail(
    requestUrl: string,
    version: number,
    error: unknown
  ): RemoteFrameErrorDetail {
    const message = error instanceof Error ? error.message : String(error);

    if (isSharedFetchHttpError(error)) {
      let hint = "Verify the URL and upstream response.";
      if (error.status === 404) {
        hint = "The URL returned 404. Verify path, query parameters, and section selectors.";
      } else if (error.status === 401 || error.status === 403) {
        hint = "The request was rejected (auth/CORS/policy). Verify access and response headers.";
      } else if (error.status >= 500) {
        hint = "The upstream server failed. Retry and verify server health.";
      }

      return {
        mode: "network",
        kind: "http",
        message,
        url: requestUrl,
        version,
        status: error.status,
        statusText: error.statusText,
        responseUrl: error.url,
        hint,
        error,
      };
    }

    if (message.toLowerCase().includes("timeout")) {
      return {
        mode: "network",
        kind: "timeout",
        message,
        url: requestUrl,
        version,
        hint: "Request timed out. Increase timeout-ms or verify upstream latency.",
        error,
      };
    }

    if (error instanceof TypeError && this.isCrossOriginUrl(requestUrl)) {
      return {
        mode: "network",
        kind: "cors-or-network",
        message,
        url: requestUrl,
        version,
        hint: "Cross-origin request failed before response. Check CORS headers for this origin.",
        error,
      };
    }

    if (error instanceof TypeError) {
      return {
        mode: "network",
        kind: "network",
        message,
        url: requestUrl,
        version,
        hint: "Network request failed before receiving a response.",
        error,
      };
    }

    return {
      mode: "network",
      kind: "unknown",
      message,
      url: requestUrl,
      version,
      hint: "Unexpected refresh failure. Inspect error payload for details.",
      error,
    };
  }

  /** Returns true when a URL resolves to a different origin than the current page. */
  private isCrossOriginUrl(url: string): boolean {
    try {
      const resolved = new URL(url, window.location.href);
      return resolved.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  /** Registers delegated in-frame trigger listeners (click/change/submit/etc). */
  private setupDelegatedListeners(): void {
    const events = splitCsv(this.getAttribute(FrameAttribute.ListenEvents) ?? "");

    this.reconcileEventListeners(this, this.delegatedHandlers, events, () => {
      return (event: Event) => {
        const rawSelector = this.getAttribute(FrameAttribute.ListenSelector);
        const selector = (rawSelector ?? DEFAULT_LISTEN_SELECTOR).trim();
        const allowDatasetFallback = rawSelector === null;
        const target = findDelegatedTarget(this, event, selector, allowDatasetFallback);
        if (!target) return;

        if (event.type === "submit") {
          const handled = this.handleAsyncSubmitEvent(event);
          if (handled) {
            return;
          }
        }

        const payload = extractPayloadFromTriggerTarget(target, event);
        this.debugLog(`trigger:${event.type} target=<${target.tagName.toLowerCase()}>`);
        const refreshTarget = payload.target;

        if (refreshTarget && !matchesRefreshTarget(this, refreshTarget, event)) {
          return;
        }

        if (shouldPreventDefaultTrigger(target, event)) {
          event.preventDefault();
          this.debugLog(`trigger:prevent-default type=${event.type}`);
        }

        if (payload.stopOnHandled) {
          event.stopPropagation();
        }

        void this.refresh(payload);
      };
    });
  }

  /** Removes all delegated trigger listeners from this frame. */
  private clearDelegatedListeners(): void {
    this.reconcileEventListeners(this, this.delegatedHandlers, [], () => () => undefined);
  }

  /** Registers delegated prefetch listeners for opt-in cache warmups. */
  private setupPrefetchListeners(): void {
    const events = splitCsv(this.getAttribute(FrameAttribute.PrefetchEvents) ?? "");

    this.reconcileEventListeners(this, this.prefetchHandlers, events, () => {
      return (event: Event) => {
        const prefetchSelector = this.getAttribute(FrameAttribute.PrefetchSelector);
        const listenSelector = this.getAttribute(FrameAttribute.ListenSelector);
        const selector = (prefetchSelector ?? listenSelector ?? DEFAULT_LISTEN_SELECTOR).trim();
        const allowDatasetFallback = prefetchSelector === null && listenSelector === null;
        const target = findDelegatedTarget(this, event, selector, allowDatasetFallback);
        if (!target) return;
        if (!hasFrameDatasetAttributes(target)) return;

        const payload = extractPayloadFromTriggerTarget(target, event);
        if (payload.sourceSelector) return;

        const effectivePayload = this.withFallbackSource(payload, true);
        const config = resolveFrameConfig(this, effectivePayload);
        if (config.cache !== "default") return;

        const requestUrl = resolveRequestUrl(config, effectivePayload);
        if (!requestUrl) return;

        this.debugLog(`prefetch:url ${requestUrl}`);
        void sharedFetchText(requestUrl, {
          cacheMode: "default",
          cacheTtlMs: config.cacheTtlMs,
          cacheMaxEntries: config.cacheMaxEntries,
          timeoutMs: config.timeoutMs,
          credentials: config.credentials,
        });
      };
    });
  }

  /** Removes all prefetch listeners from this frame. */
  private clearPrefetchListeners(): void {
    this.reconcileEventListeners(this, this.prefetchHandlers, [], () => () => undefined);
  }

  /** Registers document-level custom events that can refresh this frame. */
  private setupDocumentListeners(): void {
    const events = splitCsv(this.getAttribute(FrameAttribute.DocumentEvents) ?? "");

    this.reconcileEventListeners(document, this.documentHandlers, events, () => {
      return (event: Event) => {
        if (!(event instanceof CustomEvent)) {
          void this.refresh();
          return;
        }

        void this.refresh(normalizeRefreshPayload(event.detail));
      };
    });
  }

  /** Removes all document-level refresh listeners. */
  private clearDocumentListeners(): void {
    this.reconcileEventListeners(document, this.documentHandlers, [], () => () => undefined);
  }

  /** Intercepts delegated async-submit forms and schedules submit+refresh flow. */
  private handleAsyncSubmitEvent(event: Event): boolean {
    if (!(event.target instanceof HTMLFormElement)) {
      return false;
    }

    const submitter = this.extractSubmitter(event);
    const submitPayload = extractSubmitPayload(event.target, submitter);
    if (!submitPayload || submitPayload.mode !== "async") {
      return false;
    }

    const refreshPayload = extractPayloadFromSubmitTargets(event.target, submitter, event);
    const target = refreshPayload.target;

    if (target && !matchesRefreshTarget(this, target, event)) {
      return false;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    if (refreshPayload.stopOnHandled) {
      event.stopPropagation();
    }

    this.scheduleAsyncSubmit(event.target, submitter, submitPayload, refreshPayload);
    return true;
  }

  /** Extracts the submitter element from a submit event when available. */
  private extractSubmitter(event: Event): HTMLElement | null {
    const maybeSubmitter = (event as SubmitEvent & { submitter?: EventTarget | null }).submitter;
    if (maybeSubmitter instanceof HTMLElement) {
      return maybeSubmitter;
    }
    return null;
  }

  /** Debounces async form submissions when configured by submit payload. */
  private scheduleAsyncSubmit(
    form: HTMLFormElement,
    submitter: HTMLElement | null,
    submitPayload: RemoteFrameSubmitPayload,
    refreshPayload: RemoteFrameRefreshPayload
  ): void {
    const debounceMs = Math.max(0, submitPayload.debounceMs);
    if (debounceMs === 0) {
      void this.submitFormAsync(form, submitter, submitPayload, refreshPayload);
      return;
    }

    this.pendingSubmitContext = {
      form,
      submitter,
      submitPayload,
      refreshPayload,
    };

    if (this.submitDebounceTimerId !== null) {
      clearTimeout(this.submitDebounceTimerId);
    }

    this.submitDebounceTimerId = window.setTimeout(() => {
      this.submitDebounceTimerId = null;
      const pending = this.pendingSubmitContext;
      this.pendingSubmitContext = null;
      if (!pending || !this.isConnected) return;
      void this.submitFormAsync(
        pending.form,
        pending.submitter,
        pending.submitPayload,
        pending.refreshPayload
      );
    }, debounceMs);
  }

  /** Executes an async form submission and triggers configured post-submit refresh. */
  private async submitFormAsync(
    form: HTMLFormElement,
    submitter: HTMLElement | null,
    submitPayload: RemoteFrameSubmitPayload,
    refreshPayload: RemoteFrameRefreshPayload
  ): Promise<void> {
    this.abortActiveSubmit("Superseded by newer submit");

    const request = this.buildSubmitRequest(form, submitter, submitPayload);
    const controller = new AbortController();
    this.activeSubmitController = controller;

    const timeoutMs = Math.max(0, toNumber(this.getAttribute(FrameAttribute.TimeoutMs), 12_000));
    if (timeoutMs > 0) {
      this.activeSubmitTimeoutId = window.setTimeout(() => {
        if (this.activeSubmitController === controller && !controller.signal.aborted) {
          controller.abort(createAbortError(`Submit timeout after ${timeoutMs}ms`));
        }
      }, timeoutMs);
    }

    this.setSubmittingState(true, "submitting");
    this.dispatchEvent(
      new CustomEvent("remote-frame:submitstart", {
        detail: {
          url: request.url,
          method: request.method,
          refresh: submitPayload.refresh,
        },
      })
    );

    let submitErrorDetail: RemoteFrameSubmitErrorDetail | null = null;
    let responseStatus = 0;
    let responseUrl = request.url;

    try {
      const response = await fetch(request.url, {
        method: request.method,
        body: request.body,
        signal: controller.signal,
        credentials: request.credentials,
      });

      responseStatus = response.status;
      responseUrl = response.url || request.url;

      if (!response.ok) {
        throw {
          kind: "http",
          status: response.status,
          statusText: response.statusText,
          responseUrl,
          message: `Submit failed: ${response.status} ${response.statusText}`,
        };
      }

      const bodyText = await response.text();
      if (this.activeSubmitController !== controller || controller.signal.aborted) {
        return;
      }

      if (submitPayload.refresh === "response") {
        await this.renderSubmitResponse(bodyText, refreshPayload);
      } else if (submitPayload.refresh === "network") {
        const submitRefreshPayload = this.createSubmitRefreshPayload(refreshPayload);
        await this.refresh(submitRefreshPayload);
      }

      const broadcastConfig = resolveFrameConfig(this, {});
      const submitBroadcastPayload = this.createSubmitRefreshPayload(refreshPayload);
      const broadcastRequestUrl = this.resolveSubmitBroadcastUrl(submitBroadcastPayload);
      this.emitSubmitBroadcast(broadcastConfig, submitBroadcastPayload, broadcastRequestUrl);

      this.dispatchEvent(
        new CustomEvent("remote-frame:submitsuccess", {
          detail: {
            url: request.url,
            method: request.method,
            responseUrl,
            status: response.status,
            refresh: submitPayload.refresh,
          },
        })
      );
    } catch (error) {
      if (!isAbortError(error)) {
        submitErrorDetail = this.buildSubmitErrorDetail(request.url, request.method, error);
        this.dispatchEvent(
          new CustomEvent("remote-frame:submiterror", {
            detail: submitErrorDetail,
          })
        );
      }
    } finally {
      const isActiveSubmit = this.activeSubmitController === controller;
      if (isActiveSubmit) {
        this.clearActiveSubmitTimeout();
        this.activeSubmitController = null;
        this.setSubmittingState(false, submitErrorDetail ? "error" : "idle");
      }

      this.dispatchEvent(
        new CustomEvent("remote-frame:submitend", {
          detail: {
            url: request.url,
            method: request.method,
            responseUrl,
            status: responseStatus || undefined,
            error: submitErrorDetail,
          },
        })
      );
    }
  }

  /** Builds the submit request URL/method/body from form + submitter overrides. */
  private buildSubmitRequest(
    form: HTMLFormElement,
    submitter: HTMLElement | null,
    submitPayload: RemoteFrameSubmitPayload
  ): {
    url: string;
    method: string;
    body?: FormData;
    credentials: RequestCredentials;
  } {
    const actionAttr =
      submitter?.getAttribute("formaction") ??
      form.getAttribute("action") ??
      window.location.href;
    const methodAttr = (
      submitter?.getAttribute("formmethod") ??
      form.getAttribute("method") ??
      "GET"
    ).toUpperCase();

    const credentials = parseCredentials(this.getAttribute(FrameAttribute.Credentials));
    const method = methodAttr || "GET";

    const formData = this.buildSubmitFormData(form, submitter);
    if (method === "GET") {
      const url = new URL(actionAttr, window.location.href);
      for (const [key, value] of formData.entries()) {
        url.searchParams.append(key, typeof value === "string" ? value : value.name);
      }

      return {
        url: url.toString(),
        method,
        credentials,
      };
    }

    return {
      url: new URL(actionAttr, window.location.href).toString(),
      method,
      body: formData,
      credentials,
    };
  }

  /** Creates FormData with submitter support and a safe fallback path. */
  private buildSubmitFormData(form: HTMLFormElement, submitter: HTMLElement | null): FormData {
    try {
      if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
        return new FormData(form, submitter);
      }
      return new FormData(form);
    } catch {
      const fallback = new FormData(form);
      if (
        (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
        submitter.name
      ) {
        fallback.set(submitter.name, submitter.value);
      }
      return fallback;
    }
  }

  /** Renders a successful async-submit response directly into the frame. */
  private async renderSubmitResponse(
    bodyText: string,
    refreshPayload: RemoteFrameRefreshPayload
  ): Promise<void> {
    const config = resolveFrameConfig(this, refreshPayload);
    const resolved = resolveRenderableHtml(this, bodyText, config);
    const scrollSnapshot = captureScrollState(this, config);

    await this.patchHtml(resolved.html, config);
    restoreScrollState(this, scrollSnapshot, config);
    this.setFrameState(resolved.html.trim() ? "ready" : "empty");

    this.dispatchEvent(
      new CustomEvent("remote-frame:load", {
        detail: {
          mode: "submit-response",
          matchedSelector: resolved.matchedSelector,
        },
      })
    );

    const isInitialRender = !this.hasCompletedInitialRender;
    this.hasCompletedInitialRender = true;
    this.emitRefreshBroadcast(config, "network", refreshPayload, undefined, isInitialRender);
  }

  /** Maps submit-flow failures into a normalized error detail payload. */
  private buildSubmitErrorDetail(
    requestUrl: string,
    method: string,
    error: unknown
  ): RemoteFrameSubmitErrorDetail {
    const message = error instanceof Error ? error.message : String(error);
    const maybeHttp = error as {
      kind?: string;
      status?: number;
      statusText?: string;
      responseUrl?: string;
      message?: string;
    };

    if (maybeHttp.kind === "http") {
      return {
        kind: "http",
        message: maybeHttp.message ?? message,
        url: requestUrl,
        method,
        status: maybeHttp.status,
        statusText: maybeHttp.statusText,
        responseUrl: maybeHttp.responseUrl,
        hint: "Form submit returned a non-success status code.",
        error,
      };
    }

    if (message.toLowerCase().includes("timeout")) {
      return {
        kind: "timeout",
        message,
        url: requestUrl,
        method,
        hint: "Submit timed out. Increase timeout-ms or verify upstream latency.",
        error,
      };
    }

    if (error instanceof TypeError) {
      return {
        kind: "network",
        message,
        url: requestUrl,
        method,
        hint: "Submit request failed before receiving a response.",
        error,
      };
    }

    return {
      kind: "unknown",
      message,
      url: requestUrl,
      method,
      hint: "Unexpected submit failure. Inspect error payload for details.",
      error,
    };
  }

  /** Keeps the `source-selector` attribute in sync with a refresh payload override. */
  private syncSourceSelectorAttributeFromPayload(payload: RemoteFrameRefreshPayload): void {
    if (payload.sourceSelector === undefined) return;

    const normalized = payload.sourceSelector.trim();
    const nextValue = normalized.length ? normalized : null;
    const current = this.getAttribute(FrameAttribute.SourceSelector);

    if (current === nextValue) return;

    this.skipNextSourceSelectorAttributeRefresh = true;
    try {
      if (nextValue === null) {
        this.removeAttribute(FrameAttribute.SourceSelector);
      } else {
        this.setAttribute(FrameAttribute.SourceSelector, nextValue);
      }
    } finally {
      this.skipNextSourceSelectorAttributeRefresh = false;
    }
  }

  /** Returns whether this frame should refresh in response to a history change action. */
  private shouldRefreshFromHistoryChange(action: "push" | "replace" | "pop", nextUrl: string): boolean {
    const config = resolveFrameConfig(this, {});
    if (config.sourceSelector) return false;
    if (this.currentFrameUrl && this.currentFrameUrl === nextUrl) return false;

    const watchActions = new Set(config.listenHistory);
    if (watchActions.has(action)) {
      return true;
    }

    // Backward compatibility: when src is omitted and history is push, popstate keeps frame in sync.
    const attrSrc = (this.getAttribute(FrameAttribute.Src) ?? "").trim();
    return action === "pop" && config.history === "push" && !attrSrc;
  }

  /** Registers broadcast listeners used to relay refreshes between related frames. */
  private setupBroadcastListeners(): void {
    const config = resolveFrameConfig(this, {});
    const events: string[] = [];
    const prefix = config.broadcastEventPrefix;

    if (config.listenRefreshAny) {
      events.push(prefix);
    }

    for (let i = 0; i < config.listenRefreshFrom.length; i++) {
      const sourceId = config.listenRefreshFrom[i];
      if (!sourceId) continue;
      events.push(`${prefix}:${sourceId}`);
    }

    this.reconcileEventListeners(
      document,
      this.broadcastHandlers,
      events,
      (eventName) => {
        return (event: Event) => {
          if (!(event instanceof CustomEvent)) return;

          const detail = this.resolveBroadcastPayload(eventName, event.detail);
          if (!detail) return;
          if (!this.hasCompletedInitialRender) return;
          if (detail.sourceFrameId && detail.sourceFrameId === this.id) return;
          if (this.hasSeenBroadcastMessage(detail.messageId)) return;

          this.markBroadcastMessageSeen(detail.messageId);

          if (detail.hop >= detail.maxHop) {
            return;
          }

          this.pendingRelayContext = {
            messageId: detail.messageId,
            originFrameId: detail.originFrameId,
            hop: detail.hop,
            maxHop: detail.maxHop,
          };

          const effectivePayload = this.buildBroadcastRefreshPayload(detail, config);
          if (!effectivePayload) {
            this.pendingRelayContext = null;
            return;
          }

          void this.refresh(effectivePayload).finally(() => {
            if (this.pendingRelayContext?.messageId === detail.messageId) {
              this.pendingRelayContext = null;
            }
          });
        };
      },
      true
    );
  }

  /** Removes all broadcast listeners registered on `document`. */
  private clearBroadcastListeners(): void {
    this.reconcileEventListeners(document, this.broadcastHandlers, [], () => () => undefined);
  }

  /** Adds/removes listeners so registry matches desired event names exactly. */
  private reconcileEventListeners(
    target: EventTarget,
    registry: Map<string, EventListener>,
    desiredEvents: string[],
    createHandler: (eventName: string) => EventListener,
    rebindExisting = false
  ): void {
    const next = new Set(desiredEvents);

    for (const [eventName, handler] of registry.entries()) {
      if (next.has(eventName)) continue;
      target.removeEventListener(eventName, handler);
      registry.delete(eventName);
    }

    for (let i = 0; i < desiredEvents.length; i++) {
      const eventName = desiredEvents[i];
      if (!eventName) continue;

      if (registry.has(eventName) && !rebindExisting) continue;
      if (registry.has(eventName) && rebindExisting) {
        const existing = registry.get(eventName);
        if (existing) {
          target.removeEventListener(eventName, existing);
        }
      }

      const handler = createHandler(eventName);
      registry.set(eventName, handler);
      target.addEventListener(eventName, handler);
    }
  }

  /** Normalizes broadcast event payload data and rejects invalid messages. */
  private resolveBroadcastPayload(
    eventName: string,
    detailInput: unknown
  ): RemoteFrameBroadcastPayload | null {
    if (!detailInput || typeof detailInput !== "object") return null;

    const detail = detailInput as Partial<RemoteFrameBroadcastPayload>;
    const fromEventName = eventName.includes(":") ? eventName.split(":").slice(1).join(":") : "";
    const sourceFrameId = (detail.sourceFrameId ?? fromEventName ?? "").trim();
    if (!sourceFrameId) return null;

    const parsedMaxHop = Number(detail.maxHop);
    const parsedHop = Number(detail.hop);
    const maxHop = Number.isFinite(parsedMaxHop) ? Math.max(1, parsedMaxHop) : 1;
    const hop = Number.isFinite(parsedHop) ? Math.max(0, parsedHop) : 0;

    return {
      sourceFrameId,
      originFrameId: (detail.originFrameId ?? sourceFrameId).trim(),
      messageId: (detail.messageId ?? this.createBroadcastMessageId(sourceFrameId)).trim(),
      hop,
      maxHop,
      payload: normalizeRefreshPayload(detail.payload),
      requestUrl: typeof detail.requestUrl === "string" ? detail.requestUrl : undefined,
      from: detail.from === "proxy" ? "proxy" : "network",
    };
  }

  /** Builds the refresh payload that should run after receiving a broadcast. */
  private buildBroadcastRefreshPayload(
    detail: RemoteFrameBroadcastPayload,
    config: FrameConfig
  ): RemoteFrameRefreshPayload | null {
    const payload = normalizeRefreshPayload(detail.payload);
    const mode = config.listenRefreshUrlMode;
    const sourceUrl = (detail.requestUrl ?? payload.src ?? "").trim();

    if (mode === "full" && sourceUrl) {
      return {
        src: sourceUrl,
        cache: payload.cache,
      };
    }

    const incomingEntries = parseQueryInput(payload.query) ?? this.queryEntriesFromUrl(sourceUrl);
    if (!incomingEntries || incomingEntries.length === 0) {
      return null;
    }

    const allowedKeys = new Set(config.listenRefreshQueryKeys);
    const filteredEntries =
      allowedKeys.size === 0
        ? incomingEntries
        : incomingEntries.filter(([key]) => allowedKeys.has(key));

    if (filteredEntries.length === 0) {
      return null;
    }

    const query: Record<string, string | number | boolean | null | undefined> = {};
    for (let i = 0; i < filteredEntries.length; i++) {
      const [key, value] = filteredEntries[i];
      query[key] = value;
    }

    return {
      query,
      queryMode: mode === "query-replace" ? "replace" : "merge",
      cache: payload.cache,
    };
  }

  /** Extracts query entries from a URL string, or null for invalid inputs. */
  private queryEntriesFromUrl(urlValue: string): Array<[string, string | null]> | null {
    if (!urlValue) return null;

    try {
      const parsed = new URL(urlValue, window.location.href);
      const entries: Array<[string, string | null]> = [];
      parsed.searchParams.forEach((value, key) => {
        entries.push([key, value]);
      });
      return entries;
    } catch {
      return null;
    }
  }

  /** Emits load-driven refresh broadcasts for sibling/peer remote frames. */
  private emitRefreshBroadcast(
    config: FrameConfig,
    from: "network" | "proxy",
    payload: RemoteFrameRefreshPayload,
    requestUrl?: string,
    isInitialRender = false
  ): void {
    if (!this.shouldBroadcastLoad(config)) return;
    if (!this.id) return;
    if (isInitialRender) return;

    const relay = this.pendingRelayContext;
    const isRelay = Boolean(relay);

    if (isRelay && !config.broadcastReemit) {
      return;
    }

    const maxHop = isRelay ? relay!.maxHop : config.broadcastMaxHop;
    const hop = isRelay ? relay!.hop + 1 : 0;
    if (hop > maxHop) {
      return;
    }

    const sourceFrameId = this.id;
    const messageId = isRelay ? relay!.messageId : this.createBroadcastMessageId(sourceFrameId);
    const originFrameId = isRelay ? relay!.originFrameId : sourceFrameId;
    const eventName = `${config.broadcastEventPrefix}:${sourceFrameId}`;

    this.markBroadcastMessageSeen(messageId);

    const detail: RemoteFrameBroadcastPayload = {
      sourceFrameId,
      originFrameId,
      messageId,
      hop,
      maxHop,
      payload: normalizeRefreshPayload(payload),
      requestUrl,
      from,
    };

    document.dispatchEvent(new CustomEvent(config.broadcastEventPrefix, { detail }));
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /** Emits submit-driven refresh broadcasts for sibling/peer remote frames. */
  private emitSubmitBroadcast(
    config: FrameConfig,
    payload: RemoteFrameRefreshPayload,
    requestUrl?: string
  ): void {
    if (!this.shouldBroadcastSubmit(config)) return;
    if (!this.id) return;

    const sourceFrameId = this.id;
    const messageId = this.createBroadcastMessageId(sourceFrameId);
    const eventName = `${config.broadcastEventPrefix}:${sourceFrameId}`;

    this.markBroadcastMessageSeen(messageId);

    const detail: RemoteFrameBroadcastPayload = {
      sourceFrameId,
      originFrameId: sourceFrameId,
      messageId,
      hop: 0,
      maxHop: config.broadcastMaxHop,
      payload: normalizeRefreshPayload(payload),
      requestUrl,
      from: "network",
    };

    document.dispatchEvent(new CustomEvent(config.broadcastEventPrefix, { detail }));
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /** Returns whether load events should be broadcast based on current config. */
  private shouldBroadcastLoad(config: FrameConfig): boolean {
    return config.broadcastMode === "load" || config.broadcastMode === "load-submit";
  }

  /** Returns whether submit events should be broadcast based on current config. */
  private shouldBroadcastSubmit(config: FrameConfig): boolean {
    return config.broadcastMode === "submit" || config.broadcastMode === "load-submit";
  }

  /** Checks if a broadcast message id has already been processed by this frame. */
  private hasSeenBroadcastMessage(messageId: string): boolean {
    return this.seenBroadcastMessageIds.has(messageId);
  }

  /** Remembers a broadcast message id while bounding memory usage. */
  private markBroadcastMessageSeen(messageId: string): void {
    if (!messageId || this.seenBroadcastMessageIds.has(messageId)) return;

    this.seenBroadcastMessageIds.add(messageId);
    this.seenBroadcastQueue.push(messageId);

    const maxRemembered = 300;
    if (this.seenBroadcastQueue.length <= maxRemembered) return;

    const oldest = this.seenBroadcastQueue.shift();
    if (!oldest) return;
    this.seenBroadcastMessageIds.delete(oldest);
  }

  /** Generates a unique broadcast message id with UUID or timestamp fallback. */
  private createBroadcastMessageId(sourceFrameId: string): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${sourceFrameId}:${crypto.randomUUID()}`;
    }

    RemoteFrameElement.broadcastSequence += 1;
    return `${sourceFrameId}:${Date.now().toString(36)}:${RemoteFrameElement.broadcastSequence.toString(36)}`;
  }

  /** Starts/stops polling based on visibility, lazy state, and configured interval. */
  private updatePolling(): void {
    this.clearPollingTimer();

    const pollMs = Math.max(0, toNumber(this.getAttribute(FrameAttribute.PollMs), 0));
    if (this.hasAttribute(FrameAttribute.Lazy) && !this.hasIntersected) return;
    if (document.visibilityState === "hidden") return;
    if (!this.isConnected || pollMs <= 0) return;

    this.pollTimerId = window.setInterval(() => {
      if (!this.isConnected || this.loading || this.submitting) return;
      void this.refresh();
    }, pollMs);
  }

  /** Clears any active polling interval. */
  private clearPollingTimer(): void {
    if (this.pollTimerId === null) return;
    clearInterval(this.pollTimerId);
    this.pollTimerId = null;
  }

  /** Initializes lazy intersection observation before first network/proxy render. */
  private setupLazyObserver(): void {
    if (!this.hasAttribute(FrameAttribute.Lazy) || this.hasIntersected) return;

    if (!("IntersectionObserver" in window)) {
      this.hasIntersected = true;
      return;
    }

    this.disconnectIntersectionObserver();
    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        if (!entry.isIntersecting) continue;

        this.hasIntersected = true;
        this.disconnectIntersectionObserver();
        this.updatePolling();
        void this.refresh();
        break;
      }
    });

    this.intersectionObserver.observe(this);
  }

  /** Disconnects and releases the active intersection observer. */
  private disconnectIntersectionObserver(): void {
    if (!this.intersectionObserver) return;
    this.intersectionObserver.disconnect();
    this.intersectionObserver = null;
  }

  /** Disconnects proxy source observation and clears cached source pointers. */
  private disconnectProxyObserver(): void {
    if (this.proxyObserver) {
      this.proxyObserver.disconnect();
    }
    this.proxyObserver = null;
    this.proxySource = null;
  }

  /** Aborts active refresh fetch and resets loading state if needed. */
  private invalidateActiveRequest(reason: string): void {
    if (this.activeRequestController) {
      this.activeRequestController.abort(createAbortError(reason));
      this.activeRequestController = null;
      this.requestVersion += 1;
    }

    if (this.loading) {
      this.setLoadingState(false, this.getActiveLoadingClass());
    }
  }

  /** Aborts the active async submit request and clears submit UI state. */
  private abortActiveSubmit(reason: string): void {
    if (!this.activeSubmitController) return;
    this.activeSubmitController.abort(createAbortError(reason));
    this.activeSubmitController = null;
    this.clearActiveSubmitTimeout();
    if (this.submitting) {
      this.setSubmittingState(false, "idle");
    }
  }

  /** Clears the active submit timeout timer, if present. */
  private clearActiveSubmitTimeout(): void {
    if (this.activeSubmitTimeoutId === null) return;
    clearTimeout(this.activeSubmitTimeoutId);
    this.activeSubmitTimeoutId = null;
  }

  /** Clears pending debounced submit work. */
  private clearPendingSubmitDebounce(): void {
    if (this.submitDebounceTimerId !== null) {
      clearTimeout(this.submitDebounceTimerId);
      this.submitDebounceTimerId = null;
    }
    this.pendingSubmitContext = null;
  }

  /** Cancels any queued microtask refresh and drops queued payload state. */
  private cancelQueuedRefresh(): void {
    this.refreshQueueToken += 1;
    this.queuedRefresh = false;
    this.queuedPayload = null;
  }

  /** Returns true when `initial-fetch` explicitly requests hydration-only startup. */
  private shouldSkipInitialFetch(): boolean {
    const raw = this.getAttribute(FrameAttribute.InitialFetch);
    if (raw === null) return false;
    const value = raw.trim().toLowerCase();
    return value === "" || value === "skip" || value === "true" || value === "1" || value === "yes";
  }

  /** Derives initial ready/empty state from existing server-rendered DOM content. */
  private markHydratedFromExistingContent(): void {
    const hasRenderableContent = Array.from(this.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE) {
        return Boolean(node.textContent && node.textContent.trim());
      }
      return false;
    });

    this.setFrameState(hasRenderableContent ? "ready" : "empty");
    this.hasCompletedInitialRender = hasRenderableContent;
    this.lastRenderedHtml = hasRenderableContent ? this.innerHTML : null;
  }

  /** Resolves the active loading class with defaults applied. */
  private getActiveLoadingClass(): string {
    return (this.getAttribute(FrameAttribute.LoadingClass) ?? "is-loading").trim() || "is-loading";
  }

  /** Resolves the active submitting class with defaults applied. */
  private getActiveSubmittingClass(): string {
    return (this.getAttribute(FrameAttribute.SubmittingClass) ?? "is-submitting").trim() || "is-submitting";
  }

  /** Emits high-level debug logs only when the `debug` attribute is enabled. */
  private debugLog(message: string, detail?: unknown): void {
    if (!this.hasAttribute(FrameAttribute.Debug)) return;
    const suffix = this.id ? `#${this.id}` : "";
    const prefix = `[remote-frame${suffix}] ${message}`;
    if (typeof detail === "undefined") {
      console.debug(prefix);
      return;
    }
    console.debug(prefix, detail);
  }

  /** Injects a fallback source URL when payload and attributes omit `src`. */
  private withFallbackSource(
    payload: RemoteFrameRefreshPayload,
    allowWhenEmpty = false
  ): RemoteFrameRefreshPayload {
    if (!allowWhenEmpty && Object.keys(payload).length === 0) {
      return payload;
    }

    const payloadSrc = (payload.src ?? "").trim();
    const attrSrc = (this.getAttribute(FrameAttribute.Src) ?? "").trim();
    if (payloadSrc || attrSrc) {
      return payload;
    }

    const fallbackSrc = this.currentFrameUrl ?? window.location.href;
    return {
      ...payload,
      src: fallbackSrc,
    };
  }

  /** Creates the refresh payload used after an async submit completes. */
  private createSubmitRefreshPayload(payload: RemoteFrameRefreshPayload): RemoteFrameRefreshPayload {
    return {
      ...this.withFallbackSource(payload, true),
    };
  }

  /** Resolves the URL that should be included in submit broadcast messages. */
  private resolveSubmitBroadcastUrl(payload: RemoteFrameRefreshPayload): string | undefined {
    const effectivePayload = this.withFallbackSource(payload, true);
    const config = resolveFrameConfig(this, effectivePayload);
    if (config.sourceSelector) {
      return this.currentFrameUrl ?? undefined;
    }

    return resolveRequestUrl(config, effectivePayload) ?? this.currentFrameUrl ?? undefined;
  }

  /** Installs a global bridge that emits push/replace/pop history changes as custom events. */
  private static ensureHistoryChangeBridge(): void {
    if (RemoteFrameElement.historyChangeBridgeInstalled) return;

    RemoteFrameElement.historyChangeBridgeInstalled = true;
    RemoteFrameElement.lastHistoryUrl = window.location.href;

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = ((state: unknown, unused: string, url?: string | URL | null) => {
      const previousUrl = RemoteFrameElement.lastHistoryUrl || window.location.href;
      originalPushState(state, unused, url);
      const internal = RemoteFrameElement.internalHistoryChangeDepth > 0;
      const sourceFrameId = internal
        ? RemoteFrameElement.internalHistoryChangeSourceFrameIds[
            RemoteFrameElement.internalHistoryChangeSourceFrameIds.length - 1
          ] ?? ""
        : "";
      RemoteFrameElement.emitHistoryChange("push", previousUrl, internal, sourceFrameId);
    }) as History["pushState"];

    window.history.replaceState = ((state: unknown, unused: string, url?: string | URL | null) => {
      const previousUrl = RemoteFrameElement.lastHistoryUrl || window.location.href;
      originalReplaceState(state, unused, url);
      const internal = RemoteFrameElement.internalHistoryChangeDepth > 0;
      const sourceFrameId = internal
        ? RemoteFrameElement.internalHistoryChangeSourceFrameIds[
            RemoteFrameElement.internalHistoryChangeSourceFrameIds.length - 1
          ] ?? ""
        : "";
      RemoteFrameElement.emitHistoryChange("replace", previousUrl, internal, sourceFrameId);
    }) as History["replaceState"];

    window.addEventListener("popstate", () => {
      const previousUrl = RemoteFrameElement.lastHistoryUrl || window.location.href;
      const internal = RemoteFrameElement.internalHistoryChangeDepth > 0;
      const sourceFrameId = internal
        ? RemoteFrameElement.internalHistoryChangeSourceFrameIds[
            RemoteFrameElement.internalHistoryChangeSourceFrameIds.length - 1
          ] ?? ""
        : "";
      RemoteFrameElement.emitHistoryChange("pop", previousUrl, internal, sourceFrameId);
    });
  }

  /** Emits a normalized history-change custom event and tracks the latest URL snapshot. */
  private static emitHistoryChange(
    action: "push" | "replace" | "pop",
    previousUrl: string,
    internal: boolean,
    sourceFrameId = ""
  ): void {
    const nextUrl = window.location.href;
    RemoteFrameElement.lastHistoryUrl = nextUrl;

    window.dispatchEvent(
      new CustomEvent(RemoteFrameElement.historyChangeEventName, {
        detail: {
          action,
          internal,
          sourceFrameId,
          previousUrl,
          url: nextUrl,
        },
      })
    );
  }

  /** Runs history-mutating work while marking resulting history events as internal. */
  private runWithInternalHistoryChange(fn: () => void): void {
    RemoteFrameElement.internalHistoryChangeDepth += 1;
    RemoteFrameElement.internalHistoryChangeSourceFrameIds.push(this.id || "");
    try {
      fn();
    } finally {
      RemoteFrameElement.internalHistoryChangeDepth = Math.max(
        0,
        RemoteFrameElement.internalHistoryChangeDepth - 1
      );
      RemoteFrameElement.internalHistoryChangeSourceFrameIds.pop();
    }
  }
}

/**
 * Registers the remote-frame custom element if it is not already defined.
 *
 * @param customElementName - Tag name to register.
 */
export function defineRemoteFrame(customElementName = "remote-frame"): void {
  if (!customElements.get(customElementName)) {
    customElements.define(customElementName, RemoteFrameElement);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "remote-frame": RemoteFrameElement;
  }
}
